import { useState } from 'react'
import { toast } from 'react-toastify'
import { useImageCrop } from '@/components/image-crop'
import { uploadPartnerLogo } from '@/lib/api/partners/partners-api'
import { uploadOrganizationLogo } from '@/lib/api/organization/organization-api'
import {
    setAccessibleCompanies,
    setCredentials,
} from '@/store/slices/authSlice'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'

/**
 * Upload da logo, compartilhado pelas abas "Conta" e "Empresa".
 *
 * Era a unica coisa que as duas abas dividiam de verdade. Como hook, cada uma
 * o chama sem precisar receber `uploadingLogo`, `logoPreview` e o handler por
 * props do componente pai.
 *
 * O destino depende do tipo de conta: parceiro sobe para o endpoint do
 * parceiro; os demais, para o da organizacao — e neste caso a logo tambem e
 * propagada para todas as empresas acessiveis, senao o seletor de empresa
 * continuaria exibindo a antiga.
 */
export function useLogoUpload() {
    const auth = useAppSelector(state => state.auth)
    const { user, isPartner } = useAuth()
    const dispatch = useAppDispatch()

    const [uploadingLogo, setUploadingLogo] = useState(false)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)

    // Logo aparece em PDF (ex.: extrato de comissão), e o @react-pdf/renderer não
    // lê WebP: a saída fica em PNG, que também preserva a transparência.
    const { cropFile, cropDialog: logoCropDialog } = useImageCrop({
        title: 'Ajustar logo',
        output: { maxWidth: 512, mimeType: 'image/png' },
    })

    const handleLogoUpload = async (rawFile: File) => {
        const file = await cropFile(rawFile)
        if (!file) return
        setLogoPreview(URL.createObjectURL(file))
        // Para parceiros, upload vai para o endpoint de partner
        if (isPartner && user?.partner?.id) {
            setUploadingLogo(true)
            try {
                const response = await uploadPartnerLogo(user.partner.id, file)
                if (response.success && response.result) {
                    toast.success('Logo atualizada com sucesso')
                    const logoUrl = response.result.logoUrl
                    setLogoPreview(null)

                    dispatch(
                        setCredentials({
                            ...auth,
                            user: {
                                ...user,
                                partner: {
                                    ...user.partner,
                                    logoUrl,
                                },
                            },
                        })
                    )
                } else {
                    toast.error(response.error || 'Erro ao enviar logo')
                }
            } catch {
                toast.error('Erro ao enviar logo')
            } finally {
                setUploadingLogo(false)
            }
            return
        }

        const organizationId = user?.company?.organizationId
        if (!organizationId) {
            toast.error('Organização não encontrada')
            return
        }

        setUploadingLogo(true)
        try {
            const response = await uploadOrganizationLogo(organizationId, file)

            if (response.success && response.result) {
                toast.success('Logo atualizada com sucesso')
                const logoUrl = response.result.logoUrl
                setLogoPreview(null)

                dispatch(
                    setCredentials({
                        ...auth,
                        user: {
                            ...user,
                            company: {
                                ...user.company,
                                logoUrl,
                            } as any,
                        },
                    })
                )
                // Atualizar logoUrl em todas as companies da organização no CompanySelector
                const updatedAccessible = auth.accessibleCompanies.map(c =>
                    c.organizationId === organizationId ? { ...c, logoUrl } : c
                )
                dispatch(setAccessibleCompanies(updatedAccessible))
            } else {
                toast.error(response.error || 'Erro ao enviar logo')
            }
        } catch {
            toast.error('Erro ao enviar logo')
        } finally {
            setUploadingLogo(false)
        }
    }

    return { uploadingLogo, logoPreview, setLogoPreview, handleLogoUpload, logoCropDialog }
}
